import { safeGetSession } from './supabase';
import { getAccessToken } from './authStore';
import type { CheckoutMetaContext } from './metaEvents';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

export type BillingStatus = {
  userId: string;
  plan: string;
  isPro: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
};

async function getJwtOrThrow(): Promise<string> {
  const token =
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  return token;
}

export const billingService = {
  async getStatus(): Promise<BillingStatus> {
    if (!apiBaseUrl) {
      throw new Error('Missing REACT_APP_API_BASE_URL');
    }
    const token = await getJwtOrThrow();
    const res = await fetch(`${apiBaseUrl}/billing/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`BILLING_STATUS_${res.status}${text ? `: ${text}` : ''}`);
    }
    return res.json() as Promise<BillingStatus>;
  },

  async createCheckout(priceId: string, meta?: CheckoutMetaContext): Promise<{ url: string }> {
    if (!apiBaseUrl) {
      throw new Error('Missing REACT_APP_API_BASE_URL');
    }
    const token = await getJwtOrThrow();
    // Standard SaaS: avoid infinite loading on network hangs
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let res: Response;
    try {
      res = await fetch(`${apiBaseUrl}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ priceId, meta }),
        signal: controller.signal,
      });
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        throw new Error('BILLING_CHECKOUT_TIMEOUT');
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
    const bodyText = await res.text().catch(() => '');
    if (!res.ok) {
      // Try to parse error response and extract code
      try {
        const json = JSON.parse(bodyText);
        if (json?.code) {
          throw new Error(String(json.code));
        }
        if (json?.message) {
          throw new Error(String(json.message));
        }
      } catch (parseError) {
        // If it's already an Error we threw above, rethrow it
        if (parseError instanceof Error && !parseError.message.includes('JSON')) {
          throw parseError;
        }
        // Otherwise it's a JSON parse error, fall through to generic error
      }
      throw new Error(`BILLING_CHECKOUT_${res.status}${bodyText ? `: ${bodyText}` : ''}`);
    }
    try {
      return JSON.parse(bodyText) as { url: string };
    } catch {
      throw new Error('Invalid checkout response');
    }
  },

  async createPortalSession(): Promise<{ url: string }> {
    if (!apiBaseUrl) {
      throw new Error('Missing REACT_APP_API_BASE_URL');
    }
    const token = await getJwtOrThrow();
    const res = await fetch(`${apiBaseUrl}/billing/portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    const bodyText = await res.text().catch(() => '');
    if (!res.ok) {
      try {
        const json = JSON.parse(bodyText);
        if (json?.code) throw new Error(String(json.code));
        if (json?.message) throw new Error(String(json.message));
      } catch {
        // ignore
      }
      throw new Error(`BILLING_PORTAL_${res.status}${bodyText ? `: ${bodyText}` : ''}`);
    }
    try {
      return JSON.parse(bodyText) as { url: string };
    } catch {
      throw new Error('Invalid portal response');
    }
  },
};







